/**
 * SignatureProgress - Multi-sig threshold visualization
 * 
 * Shows signature collection progress with:
 * - Progress bar with threshold marker
 * - Individual signer avatars
 * - Normal vs Critical threshold indication
 * 
 * Part of #260 Governance Hub
 */

import './SignatureProgress.css';

interface Signature {
  signer: string;
  signedAt: string;
  walletId: string;
}

interface SignatureProgressProps {
  current: number;
  required: number;
  total: number;
  threshold: 'normal' | 'critical';
  signatures?: Signature[];
  compact?: boolean;
}

export default function SignatureProgress({
  current,
  required,
  total,
  threshold,
  signatures = [],
  compact = false,
}: SignatureProgressProps) {
  const percentage = Math.min((current / required) * 100, 100);
  const isComplete = current >= required;
  
  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getInitials = (name: string) => {
    return name
      .split(/[@.\s]/)
      .filter(Boolean)
      .slice(0, 2)
      .map(s => s[0].toUpperCase())
      .join('');
  };

  if (compact) {
    return (
      <div className="signature-progress compact">
        <div className="progress-bar-container">
          <div 
            className={`progress-bar ${threshold} ${isComplete ? 'complete' : ''}`}
            style={{ width: `${percentage}%` }}
          />
          <div 
            className="threshold-marker"
            style={{ left: `${(required / total) * 100}%` }}
          />
        </div>
        <span className={`progress-label ${isComplete ? 'complete' : ''}`}>
          {current}/{required}
          <span className={`threshold-badge ${threshold}`}>
            {threshold}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className="signature-progress">
      {/* Progress Header */}
      <div className="progress-header">
        <span className="progress-count">
          <strong>{current}</strong> of <strong>{required}</strong> signatures
        </span>
        <span className={`threshold-type ${threshold}`}>
          {threshold === 'critical' ? 'Critical Threshold' : 'Normal Threshold'}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar-wrapper">
        <div className="progress-bar-container full">
          <div 
            className={`progress-bar ${threshold} ${isComplete ? 'complete' : ''}`}
            style={{ width: `${percentage}%` }}
          />
          {/* Threshold marker */}
          <div 
            className="threshold-marker"
            style={{ left: `${(required / total) * 100}%` }}
            title={`${required} signatures required`}
          >
            <span className="marker-label">{required}</span>
          </div>
        </div>
        <div className="progress-scale">
          <span>0</span>
          <span>{total}</span>
        </div>
      </div>

      {/* Signers */}
      {signatures.length > 0 && (
        <div className="signers-section">
          <h5>Signers</h5>
          <div className="signers-list">
            {signatures.map((sig, index) => (
              <div key={index} className="signer-item">
                <div className="signer-avatar" title={sig.signer}>
                  {getInitials(sig.signer)}
                </div>
                <div className="signer-info">
                  <span className="signer-name">{sig.signer}</span>
                  <span className="signer-time">{formatTimeAgo(sig.signedAt)}</span>
                </div>
                <div className="signer-wallet" title={sig.walletId}>
                  {sig.walletId.slice(0, 8)}...
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remaining slots */}
      {current < required && (
        <div className="remaining-slots">
          <span className="remaining-count">
            {required - current} more signature{required - current !== 1 ? 's' : ''} needed
          </span>
          <div className="remaining-avatars">
            {Array.from({ length: required - current }).map((_, i) => (
              <div key={i} className="signer-avatar empty">?</div>
            ))}
          </div>
        </div>
      )}

      {/* Complete indicator */}
      {isComplete && (
        <div className="complete-indicator">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M13 4L6 11l-3-3" />
          </svg>
          <span>Threshold reached</span>
        </div>
      )}
    </div>
  );
}
